import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store';
import { usePermission } from '../hooks/usePermission';
import AppLayout from '../components/layout/AppLayout';
import PublicLayout from '../components/layout/PublicLayout';
import PageLoader from '../components/common/PageLoader';
import GlobalLoadingBar from '../components/common/GlobalLoadingBar';
import PWAUpdatePrompt from '../components/common/PWAUpdatePrompt';
import OfflineBanner from '../components/common/OfflineBanner';
import { fetchMaintenanceStatus } from '../store/slices/appSlice';
import MaintenancePage from '../pages/maintenance/MaintenancePage';
import { UserRole } from '../types';

// ── Lazy page imports ─────────────────────────────────────────────────────────

// Auth
const LoginPage                = React.lazy(() => import('../pages/auth/LoginPage'));
const RegisterPage             = React.lazy(() => import('../pages/auth/RegisterPage'));
const ForgotPasswordPage       = React.lazy(() => import('../pages/auth/ForgotPasswordPage'));
const ResetPasswordPage        = React.lazy(() => import('../pages/auth/ResetPasswordPage'));
const VerifyEmailPage          = React.lazy(() => import('../pages/auth/VerifyEmailPage'));
const EmployerUnderReviewPage  = React.lazy(() => import('../pages/auth/EmployerUnderReviewPage'));

// Browseable pages (shown in AppLayout when auth, PublicLayout when not)
const HomePageSelector    = React.lazy(() => import('../pages/home/HomePage'));
const PublicJobsPage      = React.lazy(() => import('../pages/jobs/PublicJobsPage'));
const PublicJobDetailPage = React.lazy(() => import('../pages/jobs/PublicJobDetailPage'));
const JobShortLinkPage    = React.lazy(() => import('../pages/jobs/JobShortLinkPage'));
const JobLandingPage      = React.lazy(() => import('../pages/jobs/JobLandingPage'));
const CompaniesPage       = React.lazy(() => import('../pages/companies/CompaniesPage'));
const CompanyDetailPage   = React.lazy(() => import('../pages/companies/CompanyDetailPage'));
const AboutPage           = React.lazy(() => import('../pages/about/AboutPage'));
const PrivacyPage         = React.lazy(() => import('../pages/legal/PrivacyPage'));
const TermsPage           = React.lazy(() => import('../pages/legal/TermsPage'));
const ContactPage         = React.lazy(() => import('../pages/public/ContactPage'));
const FaqPage             = React.lazy(() => import('../pages/public/FaqPage'));
const HowItWorksPage      = React.lazy(() => import('../pages/public/HowItWorksPage'));
const PricingPage         = React.lazy(() => import('../pages/public/PricingPage'));
const ForEmployersPage    = React.lazy(() => import('../pages/public/ForEmployersPage'));

// Auth-only pages (always AppLayout)
const AtsPage                    = React.lazy(() => import('../pages/ats/AtsPage'));
const DashboardPage              = React.lazy(() => import('../pages/dashboard/DashboardPage'));
const SuperDashboardPage         = React.lazy(() => import('../pages/dashboard/SuperDashboardPage'));
const CreateEditJobPage          = React.lazy(() => import('../pages/jobs/CreateEditJobPage'));
const MyApplicationsPage         = React.lazy(() => import('../pages/applications/MyApplicationsPage'));
const JobAlertsPage              = React.lazy(() => import('../pages/jobs/JobAlertsPage'));
const ReceivedApplicationsPage   = React.lazy(() => import('../pages/applications/ReceivedApplicationsPage'));
const CompanyApplicationsPage    = React.lazy(() => import('../pages/applications/CompanyApplicationsPage'));
const ProfilePage                = React.lazy(() => import('../pages/profile/ProfilePage'));
const ChangePasswordPage         = React.lazy(() => import('../pages/profile/ChangePasswordPage'));
const MyCompanyPage              = React.lazy(() => import('../pages/companies/MyCompanyPage'));
const EmployerJobsPage           = React.lazy(() => import('../pages/jobs/EmployerJobsPage'));
const MyPersonalJobsPage         = React.lazy(() => import('../pages/jobs/MyPersonalJobsPage'));
const CompanyDashboardPage       = React.lazy(() => import('../pages/dashboard/CompanyDashboardPage'));
const UsersPage                  = React.lazy(() => import('../pages/admin/UsersPage'));
const SettingsPage               = React.lazy(() => import('../pages/admin/SettingsPage'));
const RightsPage                 = React.lazy(() => import('../pages/admin/RightsPage'));
const LocationsPage              = React.lazy(() => import('../pages/admin/LocationsPage'));
const AdminQuizPage              = React.lazy(() => import('../pages/admin/AdminQuizPage'));
const QuizPage                   = React.lazy(() => import('../pages/quiz/QuizPage'));
const QuizHistoryPage            = React.lazy(() => import('../pages/quiz/QuizHistoryPage'));
const EducationPage              = React.lazy(() => import('../pages/profile/EducationPage'));
const CertificationsPage         = React.lazy(() => import('../pages/profile/CertificationsPage'));
const ApplicantsPage             = React.lazy(() => import('../pages/applicants/ApplicantsPage'));
const ApplicantProfilePage       = React.lazy(() => import('../pages/applicants/ApplicantProfilePage'));
const InterviewQuestionsPage     = React.lazy(() => import('../pages/interview/InterviewQuestionsPage'));
const CommunityInterviewPage     = React.lazy(() => import('../pages/interview/CommunityInterviewPage'));
const AdminInterviewPage         = React.lazy(() => import('../pages/admin/AdminInterviewPage'));
const AdminIndustriesPage        = React.lazy(() => import('../pages/admin/AdminIndustriesPage'));
const AdminCompaniesPage         = React.lazy(() => import('../pages/admin/AdminCompaniesPage'));
const AdminJobsPage              = React.lazy(() => import('../pages/admin/AdminJobsPage'));
const AuditHistoryPage                = React.lazy(() => import('../pages/admin/AuditHistoryPage'));
const NotificationTemplatesPage       = React.lazy(() => import('../pages/admin/NotificationTemplatesPage'));
const ExternalJobsImportPage          = React.lazy(() => import('../pages/admin/ExternalJobsImportPage'));

// ── 404 ───────────────────────────────────────────────────────────────────────

const NotFoundPage: React.FC = () => (
  <div style={{ textAlign: 'center', padding: '120px 24px' }}>
    <div style={{ fontSize: 80, fontWeight: 900, color: '#56B0FF', lineHeight: 1 }}>404</div>
    <div style={{ fontSize: 24, fontWeight: 700, marginTop: 20, color: '#0f172a' }}>Page not found</div>
    <div style={{ color: '#64748b', marginTop: 10, fontSize: 16 }}>
      The page you&apos;re looking for doesn&apos;t exist or has been moved.
    </div>
    <a href="/" style={{ display: 'inline-block', marginTop: 28, color: '#56B0FF', fontWeight: 600, fontSize: 15 }}>
      ← Back to Home
    </a>
  </div>
);

// ── Layout selectors ──────────────────────────────────────────────────────────

/**
 * Renders AppLayout for authenticated users and PublicLayout for guests.
 * Used for pages that are publicly accessible but should stay within the
 * authenticated shell once a user has logged in (e.g. job browsing).
 */
const AdaptiveLayout: React.FC = () => {
  const { isAuthenticated } = useAppSelector((s) => s.auth);
  if (isAuthenticated) return <AppLayout />;
  return <PublicLayout />;
};

/**
 * Renders AppLayout only for authenticated users.
 * Redirects unauthenticated visitors to /login.
 * Unapproved Employers are redirected to /under-review (handled by EmployerApprovalGuard).
 */
const ProtectedAppLayout: React.FC = () => {
  const { isAuthenticated } = useAppSelector((s) => s.auth);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <AppLayout />;
};

// ── Employer approval guard ───────────────────────────────────────────────────

/**
 * Fires a GA4 page_view event on every React Router navigation.
 * Must be inside <BrowserRouter> so useLocation is available.
 * No-ops gracefully when gtag is not loaded (e.g. ad-blockers, dev mode).
 */
const RouteTracker: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path:  location.pathname + location.search,
        page_title: document.title,
      });
    }
  }, [location]);
  return null;
};

/**
 * Redirects unapproved Employer-role users to /under-review on every page.
 * Must be placed inside <BrowserRouter> so useLocation is available.
 */
const EmployerApprovalGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAppSelector((s) => s.auth);
  const location = useLocation();

  if (
    isAuthenticated &&
    user?.role === UserRole.Employer &&
    user.isApproved === false &&
    location.pathname !== '/under-review'
  ) {
    return <Navigate to="/under-review" replace />;
  }

  return <>{children}</>;
};

// ── Route-level guards (child route wrappers) ─────────────────────────────────

interface GuardProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
  requireEmployer?: boolean;
  requireEmployerAdmin?: boolean;
  requireApplicantsAccess?: boolean;
  requireQuizAccess?: boolean;
  /** Restricts route to Applicants (JobSeeker / Student) only */
  requireApplicant?: boolean;
  /**
   * EmployerAdmin OR Admin with a company selected (acting as Company Admin).
   * SuperAdmin always passes.
   */
  requireCompanyAdmin?: boolean;
}

const RouteGuard: React.FC<GuardProps> = ({
  children,
  requireAdmin = false,
  requireSuperAdmin = false,
  requireEmployer = false,
  requireEmployerAdmin = false,
  requireApplicantsAccess = false,
  requireQuizAccess = false,
  requireApplicant = false,
  requireCompanyAdmin = false,
}) => {
  const { isAdmin, isSuperAdmin, canManageJobs, canManageCompany, canViewApplicants, canApply } = usePermission();
  const selectedCompanyId = useAppSelector((s) => s.adminContext.selectedCompanyId);
  const isPureAdmin = isAdmin() && !isSuperAdmin();

  if (requireSuperAdmin && !isSuperAdmin()) return <Navigate to="/dashboard" replace />;
  if (requireAdmin && !isAdmin()) return <Navigate to="/dashboard" replace />;
  if (requireEmployer && !canManageJobs()) return <Navigate to="/dashboard" replace />;
  if (requireEmployerAdmin && !canManageCompany() && !isAdmin()) return <Navigate to="/dashboard" replace />;
  if (requireApplicantsAccess && !canViewApplicants()) return <Navigate to="/dashboard" replace />;
  if (requireQuizAccess && !canApply() && !isAdmin()) return <Navigate to="/dashboard" replace />;
  if (requireApplicant && !canApply()) return <Navigate to="/dashboard" replace />;
  if (requireCompanyAdmin) {
    // SuperAdmin always passes; EmployerAdmin always passes; Admin only if company selected
    if (!isSuperAdmin() && !canManageCompany() && !(isPureAdmin && selectedCompanyId)) {
      return <Navigate to="/dashboard" replace />;
    }
  }
  return <>{children}</>;
};

const PublicOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAppSelector((s) => s.auth);
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

// ── Maintenance guard ─────────────────────────────────────────────────────────
/**
 * Fetches maintenance-mode status on mount and on every path change.
 * If maintenance is ON:
 *  - /login is always allowed (admins must be able to sign in)
 *  - Admin / SuperAdmin users bypass maintenance and see the site normally
 *  - Everyone else (guests + non-admin auth users) sees MaintenancePage
 */
const MaintenanceGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { isMaintenanceMode } = useAppSelector((s) => s.app);
  const { isAdmin } = usePermission();

  // Fetch on first mount; re-check on every navigation so that toggling
  // maintenance mode takes effect within one page transition.
  useEffect(() => {
    dispatch(fetchMaintenanceStatus());
  }, [dispatch, location.pathname]);

  // /login is always accessible — admins must be able to sign in
  if (location.pathname === '/login') return <>{children}</>;

  // Non-admins are blocked when maintenance mode is active.
  // We render optimistically (assume live) until the first API response
  // comes back; the switch happens within ~100 ms with no blank-screen flash.
  if (isMaintenanceMode && !isAdmin()) return <MaintenancePage />;

  return <>{children}</>;
};

// ── Router ────────────────────────────────────────────────────────────────────

const AppRouter: React.FC = () => (
  <BrowserRouter>
    <GlobalLoadingBar />
    <PWAUpdatePrompt />
    <OfflineBanner />
    <RouteTracker />
    {/* Both guards must be inside BrowserRouter to use useLocation */}
    <MaintenanceGuard>
    <EmployerApprovalGuard>
    <Routes>

      {/* ── Under-review page for unapproved Employer accounts ───────────── */}
      <Route
        path="/under-review"
        element={
          <Suspense fallback={<PageLoader />}>
            <EmployerUnderReviewPage />
          </Suspense>
        }
      />

      {/* ── Publicly browseable pages ────────────────────────────────────────
          Auth users → AppLayout (menus stay consistent)
          Guests     → PublicLayout                                          */}
      <Route element={<AdaptiveLayout />}>
        <Route index element={<HomePageSelector />} />
        <Route path="/jobs" element={<PublicJobsPage />} />
        {/* Programmatic SEO landing pages (2-segment paths — won't collide with /jobs/:id) */}
        <Route path="/jobs/skill/:slug" element={<JobLandingPage kind="skill" />} />
        <Route path="/jobs/in/:slug"    element={<JobLandingPage kind="location" />} />
        <Route path="/jobs/type/:slug"  element={<JobLandingPage kind="type" />} />
        <Route path="/jobs/:id" element={<PublicJobDetailPage />} />
        {/* Short shareable job links (/j/{code}) → redirect to canonical job URL */}
        <Route path="/j/:code" element={<JobShortLinkPage />} />
        <Route path="/companies" element={<CompaniesPage />} />
        <Route path="/companies/:id" element={<CompanyDetailPage />} />
        <Route path="/about"          element={<AboutPage />} />
        <Route path="/privacy"        element={<PrivacyPage />} />
        <Route path="/terms"          element={<TermsPage />} />
        <Route path="/contact"        element={<ContactPage />} />
        <Route path="/faq"            element={<FaqPage />} />
        <Route path="/how-it-works"   element={<HowItWorksPage />} />
        <Route path="/pricing"        element={<PricingPage />} />
        <Route path="/for-employers"  element={<ForEmployersPage />} />
      </Route>

      {/* ── Auth-only pages — always AppLayout ───────────────────────────── */}
      <Route element={<ProtectedAppLayout />}>
        <Route path="/dashboard"       element={<DashboardPage />} />
        <Route path="/super-dashboard" element={<RouteGuard requireSuperAdmin><SuperDashboardPage /></RouteGuard>} />
        <Route path="/ats"       element={<AtsPage />} />
        <Route path="/quiz"         element={<RouteGuard requireQuizAccess><QuizPage /></RouteGuard>} />
        <Route path="/quiz/history" element={<RouteGuard requireQuizAccess><QuizHistoryPage /></RouteGuard>} />

        {/* Job seekers — applicants only */}
        <Route path="/applications"          element={<RouteGuard requireApplicant><MyApplicationsPage /></RouteGuard>} />
        <Route path="/job-alerts"            element={<RouteGuard requireApplicant><JobAlertsPage /></RouteGuard>} />
        {/* Employers / Admins — personal scope (my jobs only) */}
        <Route path="/applications/received" element={<RouteGuard requireEmployer><ReceivedApplicationsPage /></RouteGuard>} />
        {/* EmployerAdmin OR Admin with selected company */}
        <Route path="/applications/company"  element={<RouteGuard requireCompanyAdmin><CompanyApplicationsPage /></RouteGuard>} />

        {/* Profile */}
        <Route path="/profile"         element={<ProfilePage />} />
        {/* /profile/score used to be a standalone page — now it's a tab in ProfilePage */}
        <Route path="/profile/score"   element={<Navigate to="/profile?tab=score" replace />} />
        <Route path="/change-password" element={<ChangePasswordPage />} />
        <Route path="/education"       element={<RouteGuard requireApplicant><EducationPage /></RouteGuard>} />
        <Route path="/certifications"  element={<RouteGuard requireApplicant><CertificationsPage /></RouteGuard>} />
        {/* /intro-video is now a tab in ProfilePage */}
        <Route path="/intro-video" element={<Navigate to="/profile?tab=intro-video" replace />} />

        {/* Company — EmployerAdmin OR Admin with selected company */}
        <Route
          path="/my-company"
          element={
            <RouteGuard requireCompanyAdmin>
              <MyCompanyPage />
            </RouteGuard>
          }
        />

        {/* Company jobs — EmployerAdmin OR Admin with selected company */}
        <Route
          path="/my-jobs"
          element={
            <RouteGuard requireCompanyAdmin>
              <EmployerJobsPage />
            </RouteGuard>
          }
        />
        <Route
          path="/my-personal-jobs"
          element={
            <RouteGuard requireEmployer>
              <MyPersonalJobsPage />
            </RouteGuard>
          }
        />
        <Route
          path="/company-dashboard"
          element={
            <RouteGuard requireCompanyAdmin>
              <CompanyDashboardPage />
            </RouteGuard>
          }
        />
        <Route
          path="/jobs/create"
          element={
            <RouteGuard requireEmployer>
              <CreateEditJobPage />
            </RouteGuard>
          }
        />
        <Route
          path="/jobs/:id/edit"
          element={
            <RouteGuard requireEmployer>
              <CreateEditJobPage />
            </RouteGuard>
          }
        />

        {/* Applicants (admins + employers) */}
        <Route
          path="/applicants"
          element={
            <RouteGuard requireApplicantsAccess>
              <ApplicantsPage />
            </RouteGuard>
          }
        />
        <Route
          path="/applicants/:id"
          element={
            <RouteGuard requireApplicantsAccess>
              <ApplicantProfilePage />
            </RouteGuard>
          }
        />

        {/* Admin — SuperAdmin only (Admins must not access these) */}
        <Route
          path="/admin/users"
          element={<RouteGuard requireSuperAdmin><UsersPage /></RouteGuard>}
        />
        <Route
          path="/admin/settings"
          element={<RouteGuard requireSuperAdmin><SettingsPage /></RouteGuard>}
        />
        <Route
          path="/admin/rights"
          element={<RouteGuard requireSuperAdmin><RightsPage /></RouteGuard>}
        />
        <Route
          path="/admin/locations"
          element={<RouteGuard requireSuperAdmin><LocationsPage /></RouteGuard>}
        />
        <Route
          path="/admin/quiz"
          element={<RouteGuard requireSuperAdmin><AdminQuizPage /></RouteGuard>}
        />
        <Route
          path="/admin/interview"
          element={<RouteGuard requireSuperAdmin><AdminInterviewPage /></RouteGuard>}
        />
        <Route
          path="/admin/industries"
          element={<RouteGuard requireSuperAdmin><AdminIndustriesPage /></RouteGuard>}
        />
        <Route
          path="/admin/companies"
          element={<RouteGuard requireSuperAdmin><AdminCompaniesPage /></RouteGuard>}
        />
        <Route
          path="/admin/jobs"
          element={<RouteGuard requireSuperAdmin><AdminJobsPage /></RouteGuard>}
        />
        <Route
          path="/admin/audit"
          element={<RouteGuard requireSuperAdmin><AuditHistoryPage /></RouteGuard>}
        />
        <Route
          path="/admin/notifications"
          element={<RouteGuard requireSuperAdmin><NotificationTemplatesPage /></RouteGuard>}
        />
        <Route
          path="/admin/external-jobs/job-data-lake"
          element={<RouteGuard requireAdmin><ExternalJobsImportPage /></RouteGuard>}
        />
        <Route
          path="/admin/external-jobs"
          element={<Navigate to="/admin/external-jobs/job-data-lake" replace />}
        />

        {/* Interview Q&A — SEO-friendly slug routes */}
        <Route path="/interview-questions"       element={<InterviewQuestionsPage />} />
        <Route path="/interview-questions/:slug" element={<InterviewQuestionsPage />} />
        {/* Backward-compat redirect for old /interview links */}
        <Route path="/interview" element={<Navigate to="/interview-questions" replace />} />

        {/* Community Interview Q&A — applicant-contributed questions */}
        <Route path="/community-interview" element={<CommunityInterviewPage />} />

        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* ── Login / Register / Auth flows (public-only) ──────────────────── */}
      <Route
        path="/login"
        element={
          <PublicOnlyRoute>
            <Suspense fallback={<PageLoader />}>
              <LoginPage />
            </Suspense>
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicOnlyRoute>
            <Suspense fallback={<PageLoader />}>
              <RegisterPage />
            </Suspense>
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicOnlyRoute>
            <Suspense fallback={<PageLoader />}>
              <ForgotPasswordPage />
            </Suspense>
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicOnlyRoute>
            <Suspense fallback={<PageLoader />}>
              <ResetPasswordPage />
            </Suspense>
          </PublicOnlyRoute>
        }
      />
      {/* Verify email is NOT PublicOnly — users who are already logged in
          on another device might click the link */}
      <Route
        path="/verify-email"
        element={
          <Suspense fallback={<PageLoader />}>
            <VerifyEmailPage />
          </Suspense>
        }
      />

      {/* Top-level 404 */}
      <Route path="*" element={<NotFoundPage />} />

    </Routes>
    </EmployerApprovalGuard>
    </MaintenanceGuard>
  </BrowserRouter>
);

export default AppRouter;
